import {
  AccessTime,
  AttachFile,
  Close,
  Email,
  Inbox,
  Person,
  Psychology,
  Reply,
  Send,
  TrendingDown,
  TrendingFlat,
  TrendingUp,
  UnsubscribeOutlined,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import axios from "axios";
import moment from "moment";
import { useEffect, useState } from "react";
import { hostname, parseError } from "../../utils";

interface InboxMessagePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  message: any;
  getToken: () => Promise<string>;
  companyId: string;
  onReply?: (message: any) => void;
  onUnsubscribe?: (email: string) => void;
}

export default function InboxMessagePreviewDialog({
  open,
  onClose,
  message,
  getToken,
  companyId,
  onReply,
  onUnsubscribe,
}: InboxMessagePreviewDialogProps) {
  const theme = useTheme();
  const [fullMessage, setFullMessage] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch full message details when dialog opens
  useEffect(() => {
    if (open && message?.id && !fullMessage) {
      loadFullMessage();
    }
  }, [open, message?.id]);

  // Reset when message changes
  useEffect(() => {
    setFullMessage(null);
    setError(null);
  }, [message?.id]);

  const loadFullMessage = async () => {
    if (!message?.id) return;

    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/inboxes/messages/${message.id}`,
        {
          params: { companyId },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setFullMessage(response.data);
    } catch (err: any) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!message) return null;

  // Use fullMessage if loaded, otherwise fall back to preview data
  const displayMessage = fullMessage || message;

  // Helper functions for classification display
  const getCategoryColor = (category: string) => {
    switch (category) {
      case "interested":
        return "primary";
      case "not_interested":
        return "error";
      case "question":
        return "info";
      case "out_of_office":
        return "warning";
      case "unsubscribe":
        return "error";
      case "meeting_request":
        return "primary";
      case "referral":
        return "primary";
      default:
        return "default";
    }
  };

  const getSentimentIcon = (sentiment: string) => {
    switch (sentiment) {
      case "positive":
        return <TrendingUp fontSize="small" color="primary" />;
      case "negative":
        return <TrendingDown fontSize="small" color="error" />;
      case "neutral":
      default:
        return <TrendingFlat fontSize="small" color="action" />;
    }
  };

  const formatCategory = (category: string) => {
    return category.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getMessageTypeInfo = (messageType: string) => {
    switch (messageType) {
      case "received":
      case "received_reply":
        return { icon: <Inbox fontSize="small" />, label: "Inbox", color: "primary" };
      case "sent":
        return { icon: <Send fontSize="small" />, label: "Sent", color: "default" };
      case "reply_sent":
        return { icon: <Reply fontSize="small" />, label: "Reply Sent", color: "primary" };
      default:
        return { icon: <Email fontSize="small" />, label: messageType, color: "default" };
    }
  };

  // Get initials from sender name or email
  const getInitials = (name?: string, email?: string) => {
    if (name) {
      const parts = name.split(" ");
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return "?";
  };

  // Generate a consistent color based on email
  const getAvatarColor = (email?: string) => {
    if (!email) return theme.palette.primary.main;
    const colors = [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      "#f44336",
      "#e91e63",
      "#9c27b0",
      "#673ab7",
      "#3f51b5",
      "#2196f3",
      "#009688",
      "#4caf50",
      "#ff9800",
      "#ff5722",
    ];
    let hash = 0;
    for (let i = 0; i < email.length; i++) {
      hash = email.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  const messageTypeInfo = getMessageTypeInfo(displayMessage.messageType);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <Chip
              icon={messageTypeInfo.icon}
              label={messageTypeInfo.label}
              size="small"
              color={messageTypeInfo.color as any}
              variant="outlined"
            />
            {displayMessage.replied && (
              <Chip
                icon={<Reply fontSize="small" />}
                label="Replied"
                color="primary"
                size="small"
                variant="filled"
              />
            )}
            {displayMessage.generatedReply && !displayMessage.replied && (
              <Chip
                icon={<Psychology fontSize="small" />}
                label="Reply Generated"
                size="small"
                sx={{
                  border: "2px solid transparent",
                  backgroundImage: `linear-gradient(${theme.palette.background.paper}, ${theme.palette.background.paper}), linear-gradient(90deg, #4285f4, #9b72cb, #d96570)`,
                  backgroundOrigin: "border-box",
                  backgroundClip: "padding-box, border-box",
                }}
              />
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {/* Email Header */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            borderColor: "divider",
          }}
        >
          <Box sx={{ display: "flex", gap: 2 }}>
            {/* Sender Avatar */}
            <Avatar
              sx={{
                width: 48,
                height: 48,
                bgcolor: getAvatarColor(displayMessage.sender),
                fontSize: "1rem",
              }}
            >
              {getInitials(displayMessage.senderName, displayMessage.sender)}
            </Avatar>

            {/* Sender Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Subject */}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 0.5,
                  wordBreak: "break-word",
                }}
              >
                {displayMessage.subject || "(No Subject)"}
              </Typography>

              {/* From */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}>
                <Person fontSize="small" color="action" />
                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                  {displayMessage.senderName || displayMessage.sender}
                </Typography>
                {displayMessage.senderName && (
                  <Typography variant="body2" color="text.secondary">
                    &lt;{displayMessage.sender}&gt;
                  </Typography>
                )}
              </Box>

              {/* To */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}>
                <Email fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  To: {displayMessage.recipient}
                </Typography>
              </Box>

              {/* Date */}
              {displayMessage.created_date && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <AccessTime fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {moment(displayMessage.created_date).format("MMMM D, YYYY at h:mm A")}
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
                    ({moment(displayMessage.created_date).fromNow()})
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Classification Section */}
          {displayMessage.replyClassification && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Psychology fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  AI Analysis:
                </Typography>
                <Tooltip
                  title={`Intent: ${displayMessage.replyClassification.intent || "Unknown"}`}
                  arrow
                >
                  <Chip
                    label={formatCategory(displayMessage.replyClassification.category)}
                    color={getCategoryColor(displayMessage.replyClassification.category) as any}
                    size="small"
                  />
                </Tooltip>
                <Tooltip title={`Sentiment: ${displayMessage.replyClassification.sentiment}`} arrow>
                  <Chip
                    icon={getSentimentIcon(displayMessage.replyClassification.sentiment)}
                    label={displayMessage.replyClassification.sentiment}
                    size="small"
                    variant="outlined"
                  />
                </Tooltip>
                {displayMessage.replyClassification.confidence && (
                  <Chip
                    label={`${Math.round(displayMessage.replyClassification.confidence)}% confident`}
                    size="small"
                    variant="outlined"
                    color="default"
                  />
                )}
                {displayMessage.replyClassification.needsHumanReview && (
                  <Chip
                    label="Needs Review"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Box>
              {displayMessage.replyClassification.keywords?.length > 0 && (
                <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                  <Typography variant="caption" color="text.secondary">
                    Keywords:
                  </Typography>
                  {displayMessage.replyClassification.keywords.map((keyword: string, index: number) => (
                    <Chip
                      key={index}
                      label={keyword}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: "0.7rem" }}
                    />
                  ))}
                </Box>
              )}
            </>
          )}

          {/* Attachments indicator */}
          {displayMessage.rawMessage?.attachments?.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AttachFile fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {displayMessage.rawMessage.attachments.length} attachment
                  {displayMessage.rawMessage.attachments.length > 1 ? "s" : ""}
                </Typography>
              </Box>
            </>
          )}
        </Paper>

        {/* Message Body */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderColor: "error.main",
            }}
          >
            <Typography color="error">{error}</Typography>
          </Paper>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            {displayMessage.rawMessage?.bodyHtml ||
            (displayMessage.body && displayMessage.body.trim().startsWith("<")) ? (
              <iframe
                srcDoc={`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body {
      margin: 0;
      padding: 16px;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      font-size: 14px;
      line-height: 1.6;
      color: #000000;
      background-color: #ffffff;
    }
    img { max-width: 100%; height: auto; }
    pre { white-space: pre-wrap; word-break: break-word; background-color: #f5f5f5; padding: 8px; border-radius: 4px; overflow: auto; }
    a { color: #1976d2; }
    blockquote { border-left: 4px solid #e0e0e0; margin: 1em 0; padding-left: 1em; color: #666666; }
  </style>
</head>
<body>${displayMessage.rawMessage?.bodyHtml || displayMessage.body}</body>
</html>`}
                sandbox="allow-same-origin"
                title="Email content"
                style={{
                  width: "100%",
                  minHeight: "200px",
                  maxHeight: "50vh",
                  border: "none",
                  display: "block",
                }}
                onLoad={(e) => {
                  // Auto-resize iframe to fit content
                  const iframe = e.target as HTMLIFrameElement;
                  if (iframe.contentDocument) {
                    const height = iframe.contentDocument.body.scrollHeight;
                    iframe.style.height = `${Math.min(height + 32, window.innerHeight * 0.5)}px`;
                  }
                }}
              />
            ) : (
              <Box sx={{ p: 3 }}>
                <Typography
                  component="pre"
                  sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "inherit",
                    margin: 0,
                    lineHeight: 1.6,
                  }}
                >
                  {displayMessage.body || displayMessage.bodyPreview || "(No content)"}
                </Typography>
              </Box>
            )}
          </Paper>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {onUnsubscribe && displayMessage.sender && (
          <Button
            color="error"
            startIcon={<UnsubscribeOutlined />}
            onClick={() => onUnsubscribe(displayMessage.sender)}
            sx={{ mr: "auto" }}
          >
            Add to unsubscribe list
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
        {onReply && (
          <Button
            variant="contained"
            startIcon={<Reply />}
            onClick={() => {
              onReply(displayMessage);
              onClose();
            }}
          >
            Reply
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
